import { motion, AnimatePresence } from 'framer-motion'
import React, { useState, useEffect } from 'react'
import CartItem from './CartItem'
import cart from '@/pages/cart'
import { useCartState, useCartDispatch } from '@/contexts/CartContext'
import Button from '../Button'
import Link from 'next/link'
import { toast } from 'react-hot-toast'
import alerts from '@/config/alerts.json'
import { useMutation } from 'react-query'
import { CouponGet } from '@/services/api/coupon'
import { applyCoupon, removeCoupon } from 'CartActions'
import CartSteps from './CartSteps'
import useAuth from '@/hooks/useAuth'
import CartConfirmationModal from './CartConfirmationModal'
import CouponForm from './CouponForm'

type Props = {
    isOpen: boolean
    onClose: () => void
    children?: React.ReactNode
}

const modalVariants = {
    hidden: {
        opacity: 0,
        scale: 0.5,
        x: '-25vw',
        y: '40vh',
    },
    visible: {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 300,
            damping: 30,
            delay: 0.1 // Wait for overlay to fade in
        }
    },
    exit: {
        opacity: 0,
        scale: 0.5,
        x: '-40vw',
        y: '40vh',
        transition: { duration: 0.2 }
    }
}

const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, transition: { duration: 0.3 } }
}

const CartModal = ({ isOpen, onClose, children }: Props) => {
    const [isModalVisible, setIsModalVisible] = useState(isOpen)
    const cart = useCartState()
    const cartDispatch = useCartDispatch()
    const { user } = useAuth('none')
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = React.useState(false)

    useEffect(() => {
        if (isOpen) {
            setIsModalVisible(true)
        } else {
            setIsModalVisible(false)
        }
    }, [isOpen])

    const handleCheckout = () => {
        if (!user) {
            toast.error(alerts.login_first)
            const user_elem = document.querySelector('.user') as HTMLAnchorElement
            if (user_elem) {
                user_elem.click()
            }
            onClose()
            return
        }
        setIsConfirmationModalOpen(true)
    }

    return (
        <>
        <AnimatePresence initial={false} onExitComplete={onClose}>
          {isOpen && isModalVisible && (
            <motion.div className="fixed inset-0 z-[105] flex items-center justify-center ">
              <motion.div
                className="absolute inset-0 bg-black bg-opacity-50"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={overlayVariants}
                onClick={() => setIsModalVisible(false)}
              />
              <motion.div
                className="relative z-10 pointer-events-auto bg-white rounded-2xl w-[90vw] max-w-[500px] max-h-[90vh] flex flex-col"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
              >
                <div className="flex items-center justify-between px-2 border-b">
                    <div className="flex items-center gap-2 p-2">
                      <span className="text-primary text-2xl font-bold p-2">السلة</span>
                      <span className="bg-gray-200 text-primary rounded px-2 text-xs leading-none ml-2 flex items-center justify-center h-6">{cart.items.length} منتجات</span>
                  </div>
                  <button onClick={onClose} className="text-4xl px-2 rounded-lg text-gray-700  bg-gray-300">×</button>
                </div>
                <CartSteps />
                <div className="flex-1 overflow-y-auto px-4">
                  {cart.items.map((item, idx) => (
                      <div key={idx} className="flex items-center mb-4">
                        <CartItem item={item} index={idx} />
                    </div>
                  ))}
                </div>
                <div className='p-4'>
                <CouponForm />
                </div>
                <div className="p-4 border-t">
                    <div className="flex justify-between mb-2">
                        <span>مجموع العناصر</span>
                        <span className="font-bold">{cart.subtotal}₪</span>
                    </div>
                    <div className="flex justify-between mb-2">
                        <span>الشحن</span>
                        <span className="font-bold">{cart.shipping_fee}₪</span>
                    </div>
                    {cart.coupon && (
                        <li className="flex items-center justify-between mt-2 mb-2">
                            <p className="text-gray-400 text-xs">
                                تخفيض
                            </p>
                            <strong className="text-sm text-red flex items-center">
                                ₪{cart.discount_total}-
                            </strong>
                        </li>
                    )}
                    <div className="flex justify-between mb-4">
                        <span className="font-bold text-primary">الطلب الكلي</span>
                        <span className="font-bold text-emerald-500">{cart.total}₪</span>
                    </div>
                    <button 
                        onClick={handleCheckout}
                        className="w-full bg-primary text-white py-3 rounded-lg font-bold mb-2"
                    >
                        إتمام الشراء
                    </button>
                    <Link href="/products" onClick={onClose}>
                        <button className="w-full border border-primary text-primary py-3 rounded-lg font-bold">مواصلة التسوق</button>
                    </Link>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <CartConfirmationModal
          isOpen={isConfirmationModalOpen}
          setIsOpen={setIsConfirmationModalOpen}
          onProceed={() => {
            setIsConfirmationModalOpen(false);
            onClose();
          }}
        />
        </>
    )
}

export default CartModal